import { types } from './viewport';

/**
 * The field somebody has just tapped, kept above the keyboard.
 *
 * `body` has carried `padding-bottom: var(--kb)` since 2026-08-24 so that a
 * field standing where the keyboard now is has somewhere to be scrolled to —
 * and nothing ever scrolled into it. The reveal was the client's, and the
 * client cannot do it: what it scrolls against is the layout viewport, which
 * does not know a keyboard is there. Measured on the owner's geometry, 393×852,
 * «Профиль» with the name field tapped: the field at 425..471, the visible band
 * 0..517, and 343 pixels of room underneath that nothing used. It reads as the
 * field being shoved against the keys — «оно чуть-чуть прячется», 2026-09-02 —
 * and at a keyboard forty pixels taller it is under them.
 *
 * `Sheet` grew its own answer to this the same day and keeps it: the scroller
 * there is `.sheet-body`, which is a different box with a different distance to
 * leave, and the sheet's own resizing - a wheel unfolding under the field - is
 * an event nothing outside it can see. So a field inside a sheet is left to it,
 * and this is every other field in the app.
 *
 * The distance itself is not here: `scroll-padding-bottom` on the root says how
 * much of the bottom of the page is not worth scrolling something into, and
 * `--kb` is what covers it. That keeps the number where every other distance in
 * this app is - see «--kb» in index.css.
 */
export function revealFocus(): () => void {
  const reveal = (): void => {
    const focused = document.activeElement;
    if (!(focused instanceof HTMLElement) || !types(focused)) return;
    // The sheet's, and it has its own reasons. See the note above.
    if (focused.closest('.sheet') !== null) return;
    focused.scrollIntoView({ block: 'nearest' });
  };

  // A frame after the focus rather than in it: `followViewport` writes the box
  // the keyboard is about to leave on the same event, and what has to fit is
  // the field inside that box rather than inside the one there is now.
  const onFocusIn = (): void => {
    window.requestAnimationFrame(reveal);
  };
  document.addEventListener('focusin', onFocusIn);

  /**
   * And again whenever the page's own box changes, which is the half a client
   * that shortens the window needs.
   *
   * There the anticipation is right and the reveal is not needed - the field is
   * inside the band both before and after - until the window actually shortens
   * half a second later, and no browser re-reveals a field for that. `body` is
   * the one box that moves on both kinds of client: its border box grows by
   * `--kb` where the keyboard covers, and follows the window down where the
   * client shortens it.
   */
  const watcher = new ResizeObserver(reveal);
  watcher.observe(document.body);

  return () => {
    document.removeEventListener('focusin', onFocusIn);
    watcher.disconnect();
  };
}
